import React, { useState, useEffect } from 'react';
import { ProductsManagement } from '../../components/admin/organisms';
import { Product, ProductCollection } from '../../types';
import { 
  getProducts, 
  getCollections, 
  createProduct, 
  updateProduct, 
  deleteProduct,
  duplicateProduct,
  createCollection
} from '../../services/firebase';
import './ProductsPage.css';

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [collections, setCollections] = useState<ProductCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('all');

  // Fetch products and collections on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch collections first
        const collectionsData = await getCollections();
        setCollections(collectionsData);
        
        // If no collections exist, create default ones
        if (collectionsData.length === 0) {
          await createDefaultCollections();
          const updatedCollections = await getCollections();
          setCollections(updatedCollections);
        }
        
        // Fetch products
        const productsData = await getProducts();
        setProducts(productsData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load products. Please try again.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Create default collections if none exist
  const createDefaultCollections = async () => {
    const defaultCollections: Partial<ProductCollection>[] = [
      { name: 'Everyday Cakes', description: 'Cakes for birthday celebrations' },
      { name: 'Cake & Cupcake Set', description: 'Sets of cake & cupcakes for all occasions' },
      { name: 'Celebration Cakes', description: 'Cakes for special celebrations' },
      { name: 'Custom Cake Flavours', description: 'Custom flavors for cakes' },
      { name: 'Create Your Own Cake', description: 'Design your own custom cake' },
      { name: 'Desserts', description: 'Desserts & sweet treats' }
    ];
    
    try {
      await Promise.all(defaultCollections.map(collection => createCollection(collection)));
    } catch (err) {
      console.error('Error creating default collections:', err);
    }
  };

  // Handle creating a new product
  const handleCreateProduct = async (productData: Partial<Product>) => {
    try {
      setLoading(true);
      const newProduct = await createProduct(productData);
      setProducts([newProduct, ...products]);
      setLoading(false);
    } catch (err) {
      console.error('Error creating product:', err);
      setError('Failed to create product. Please try again.');
      setLoading(false);
    }
  };

  // Handle updating an existing product
  const handleUpdateProduct = async (product: Product) => {
    try {
      setLoading(true);
      await updateProduct(product.id, product);
      
      // Update products list
      const updatedProducts = products.map(p => 
        p.id === product.id ? product : p
      );
      
      setProducts(updatedProducts);
      setLoading(false);
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product. Please try again.');
      setLoading(false);
    }
  };

  // Handle deleting a product
  const handleDeleteProduct = async (product: Product) => {
    try {
      setLoading(true);
      await deleteProduct(product.id);
      
      // Remove product from list
      const updatedProducts = products.filter(p => p.id !== product.id);
      setProducts(updatedProducts);
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product. Please try again.');
      setLoading(false);
    }
  };

  // Handle duplicating a product
  const handleDuplicateProduct = async (product: Product) => {
    try {
      setLoading(true);
      const duplicatedProduct = await duplicateProduct(product.id);
      
      // Add the duplicated product to the list
      setProducts(prevProducts => [...prevProducts, duplicatedProduct]);
      
      setLoading(false);
    } catch (err) {
      console.error('Error duplicating product:', err);
      setError('Failed to duplicate product. Please try again.');
      setLoading(false);
    }
  };

  // Filter products by selected collection
  const filteredProducts = selectedCollectionId === 'all' 
    ? products 
    : products.filter(product => product.collectionId === selectedCollectionId);

  // Handle collection filter change
  const handleCollectionFilterChange = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
  };

  // Handle refreshing products
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const productsData = await getProducts();
      setProducts(productsData);
      setLoading(false);
    } catch (err) {
      console.error('Error refreshing products:', err);
      setError('Failed to refresh products. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="products-page">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      <ProductsManagement
        products={filteredProducts}
        collections={collections}
        loading={loading}
        onCreateProduct={handleCreateProduct}
        onUpdateProduct={handleUpdateProduct}
        onDeleteProduct={handleDeleteProduct}
        onDuplicateProduct={handleDuplicateProduct}
        onRefresh={handleRefresh}
        selectedCollectionId={selectedCollectionId}
        onCollectionFilterChange={handleCollectionFilterChange}
      />
    </div>
  );
};

export default ProductsPage;
